import {
    yesNoToBoolean
} from '../utils/utils';

import {
    getDeposit,
    getInvoiceAmount,
    getOrganisationType,
    getPrivateSales,
    getProductType,
    getPropertyOwner,
    getTransactionType,
} from '../utils/application-details-utils';
import {Application, ApplicationTypes, } from '../domain/application-service.domain';
import {
    constants,
}  from '../const';
import {AzureStorageDocument, DocumentTag, Metadata,} from '../domain/types.domain';
import {DocumentNotificationData, GroupedDocument, NotificationGroupedDocuments} from '../domain/component/document-details-component.domain';
import {isLegacyTrustDeedValue} from '../domain/trust-deed-condition.domain';

export const getStandardConditionByApplication = (application: Application): DocumentTag[] => {
    const isPrivateSale = yesNoToBoolean(getPrivateSales(application));
    const organisationType = getOrganisationType(application);
    const invoicePrice = getInvoiceAmount(application);
    const deposit = Number(getDeposit(application) ?? 0);
    const transactionType = getTransactionType(application);
    const productType = getProductType(application);
    const assetCategory = (application.AppInfo as any).AssetCategory ?? "";
    const assetType = (application.AppInfo as any).AssetType ?? "";
    const isAssetFinance = application.ApplicationType === 'Consumer' || application.ApplicationType === 'AssetFinance';

    const standardCondition = [
        constants.documentTypes.contract,
        constants.documentTypes.driversLicence,
    ];

    if (isAssetFinance) {

        // did not change anything for private sale and dealer sale
        if (transactionType === 'Private Sale' || transactionType === 'Dealer Sale') {
            standardCondition.push(constants.documentTypes.taxInvoice);
            if (!(invoicePrice - deposit < 100000)) {
                // required noting asset identifiers and A.C.N. 603 273 365 PL (Non-VIN assets <$100k excluded)
                standardCondition.push(constants.documentTypes.certificateOfCurrency);
            }
            if (isPrivateSale) {
                if (deposit) {
                    standardCondition.push(constants.documentTypes.depositReceipt);
                }
                standardCondition.push(constants.documentTypes.proofOfOwnership);
                standardCondition.push(constants.documentTypes.deedOfRelease);
                standardCondition.push(constants.documentTypes.specificCharges);
                standardCondition.push(constants.documentTypes.proofOfSupplierBankDetails);
            } else {
                if (deposit) {
                    standardCondition.push({
                        ...constants.documentTypes.depositReceipt,
                        description: constants.documentTypes.depositReceipt.subDescription,
                    });
                }
            }

            if (isPrivateSale && invoicePrice <= 150000) {
                standardCondition.push(constants.documentTypes.assetInspection);
            }

            if (isPrivateSale && invoicePrice > 150000) {
                standardCondition.push(constants.documentTypes.externalDesktopValuation);
            }
        } else if (transactionType === 'Sales and Lease Back') {
            standardCondition.push(constants.documentTypes.specificCharges);
            standardCondition.push(constants.documentTypes.proofOfOwnership);
            standardCondition.push(constants.documentTypes.confirmationOfAssetAcquisition);
            standardCondition.push(constants.documentTypes.deedOfRelease);
            standardCondition.push(constants.documentTypes.assetInspection);
            if (!(invoicePrice - deposit < 100000)) {
                // required noting asset identifiers and A.C.N. 603 273 365 PL (Non-VIN assets <$100k excluded)
                standardCondition.push(constants.documentTypes.certificateOfCurrency);
            }
        } else if (transactionType === 'Capital Raise') {
            standardCondition.push(constants.documentTypes.specificCharges);
            standardCondition.push(constants.documentTypes.proofOfOwnership);
            standardCondition.push(constants.documentTypes.deedOfRelease);
            standardCondition.push(constants.documentTypes.assetInspectionOnSite);
            standardCondition.push(constants.documentTypes.notExceedForcedLiquidation);
            if (!(invoicePrice - deposit < 100000)) {
                // required noting asset identifiers and A.C.N. 603 273 365 PL (Non-VIN assets <$100k excluded)
                standardCondition.push(constants.documentTypes.certificateOfCurrency);
            }
        } else if (transactionType === 'Refinance') {
            standardCondition.push(constants.documentTypes.proofOfOwnership);
            standardCondition.push(constants.documentTypes.deedOfRelease);
            standardCondition.push(constants.documentTypes.assetInspectionOnSite);
            standardCondition.push(constants.documentTypes.notExceedForcedLiquidation);
            standardCondition.push(constants.documentTypes.disburseFundsToIncumbentFinancier);
            if (!(invoicePrice - deposit < 100000)) {
                // required noting asset identifiers and A.C.N. 603 273 365 PL (Non-VIN assets <$100k excluded)
                standardCondition.push(constants.documentTypes.certificateOfCurrency);
            }
        } else if (transactionType === 'Balloon Refinance') {
            standardCondition.push(constants.documentTypes.proofOfOwnership);
            standardCondition.push(constants.documentTypes.deedOfRelease);
            standardCondition.push(constants.documentTypes.assetInspectionOnSite);
            standardCondition.push(constants.documentTypes.notExceedForcedLiquidation);
            standardCondition.push(constants.documentTypes.disburseFundsToIncumbentFinancier);
            standardCondition.push(constants.documentTypes.loanStatementFromIncumbentFinancier);
            if (!(invoicePrice - deposit < 100000)) {
                // required noting asset identifiers and A.C.N. 603 273 365 PL (Non-VIN assets <$100k excluded)
                standardCondition.push(constants.documentTypes.certificateOfCurrency);
            }
        }
    }

    if (organisationType === 'Partner') {
        standardCondition.push(constants.documentTypes.certifiedPartnershipAgreement);
    }

    if (application.DocumentTypes && application.DocumentTypes.length) {
        const conditionalAlerts = application.DocumentTypes.filter(d => !!d?.conditionalAlert);
        for (const conditionalAlert of conditionalAlerts) {
            standardCondition.push({
                type: conditionalAlert.type,
                value: conditionalAlert.value,
                description: conditionalAlert?.description ?? '',
                standardCondition: true,
                required: true,
            })
        }

        // One row per entity requiring a deed. Derived on the backend from the application's
        // entities and KYB results, and stored here so the wizard step and the Documents tab
        // render the same set. A trust no longer emits the global trustDeed condition.
        const trustDeedConditions = application.DocumentTypes.filter(d => !!d?.trustDeedCondition);
        for (const trustDeedCondition of trustDeedConditions) {
            standardCondition.push({
                ...trustDeedCondition,
                description: trustDeedCondition?.description ?? '',
                standardCondition: true,
                required: true,
            })
        }
    }

    const propertyOwnership = constants.documentTypes.ProofOfPropertyOwnership;
    if (
        getPropertyOwner(application) === 'Yes' &&
        !standardCondition.some(tag => tag.value === propertyOwnership.value || tag.type === propertyOwnership.type)
    ) {
        standardCondition.push({
            ...propertyOwnership,
            standardCondition: true,
            required: true,
        });
    }

    return standardCondition;
}

export const groupStandardCondition = (groups: GroupedDocument[], application: Application): GroupedDocument[] => {
    const standardCondition = getStandardConditionByApplication(application);
    const standardConditionValues = standardCondition.map(t => t.value);
    const groupWithStandardCondition: GroupedDocument[] = [];
    for (const group of groups) {
        const index = standardConditionValues.indexOf(group.value);
        if (index > -1) {
            groupWithStandardCondition.push({
                ...group,
                ...standardCondition[index],
                required: true,
            })
        }
        else {
            groupWithStandardCondition.push({
                ...group,
                standardCondition: false,
            })
        }
    }

    for (const condition of standardCondition) {
        const groupValues = groupWithStandardCondition.map(g => g.value);
        if (!groupValues.includes(condition.value)) {
            groupWithStandardCondition.push({
                groupName: condition.type,
                description: condition?.description ?? '',
                value: condition.value,
                required: true,
                disableDocumentUpload: condition?.disableDocumentUpload ?? false,
                standardCondition: true,
                status: 'Pending',
                docs: []
            })
        }
    }
    // Deeds uploaded before per-entity conditions existed carry the generic trustdeed tag and are
    // not migrated, so they keep a single generic box. It is shown only when it actually holds
    // something, so applications created from now on never display an empty legacy box alongside
    // their per-entity ones.
    const withLegacyTrustDeed = groupWithStandardCondition.filter(group => {
        if (!isLegacyTrustDeedValue(group.value)) {
            return true;
        }
        return group.docs.length > 0;
    }).map(group => isLegacyTrustDeedValue(group.value) ? {
        ...group,
        groupName: constants.documentTypes.trustDeed.type,
        description: constants.documentTypes.trustDeed.description ?? '',
        required: true,
        standardCondition: true,
    } : group);

    return withLegacyTrustDeed.sort((a, b) => {
        if (a.required !== b.required) {
            return a.required ? -1 : 1;
        } else {
            return a.groupName.localeCompare(b.groupName);
        }
    });
}

export const displayDocumentTag = (name: string): string => {
    const allTags: DocumentTag[] = Object.values(constants.documentTypes);
    const tagObj = allTags.find(t => t.value === name);
    if (tagObj) {
        return tagObj.type
    }
    return name;
}

export const unclassifiedDocuments = (): DocumentTag => {
    return constants.unclassifiedDocuments;
}

export const defaultDocuments = (
    applicationType: ApplicationTypes,
    guarantors: string[] = [],
    customerName: string,
    assetCategory: string,
    assetType: string,
    loanAmount: number | undefined,
    deposit: number | undefined,
    isPrivateSale: boolean = false,
    isTrust: boolean = false,
    isPartnership: boolean = false,
    includeOptionalTypes: boolean = false,
): DocumentTag[] => {
    const guarantorNames = guarantors.join(', ');

    const docs: DocumentTag[] = [
        {
            ...constants.documentTypes.privacyConsent,
            description: constants.documentTypes.privacyConsent.description +
                (applicationType == 'Consumer' ? customerName : guarantorNames) + ' ' +
                constants.documentTypes.privacyConsent.subDescription
        },
        constants.documentTypes.driversLicence,
        constants.documentTypes.bankStatements,
        constants.documentTypes.financials,
        constants.documentTypes.atoActivityStatements,
        constants.documentTypes.assetLiabilitiesStatement,
        constants.documentTypes.ppsr,
        constants.documentTypes.brokerInvoices,
        constants.documentTypes.internalDocuments,
        constants.documentTypes.creditorWatch,
        constants.documentTypes.ProofOfPropertyOwnership,
        constants.documentTypes.MortgageStatements,
        constants.documentTypes.ComparableCreditStatements,
        constants.documentTypes.ComparableLoanStatements,
        constants.documentTypes.BusinessUseConfirmation,
        constants.documentTypes.MedicareCard,
        constants.documentTypes.ReviewOfFinalInvoice,
        constants.documentTypes.ExternalOnsiteValuation,
        constants.documentTypes.approvalNotice,
        constants.documentTypes.assetInspection,
        constants.documentTypes.disburseFundsToIncumbentFinancier,
        constants.documentTypes.loanStatementFromIncumbentFinancier,
        constants.documentTypes.confirmationOfAssetAcquisition,
        constants.documentTypes.assetInspectionOnSite,
        constants.documentTypes.notExceedForcedLiquidation,
        constants.documentTypes.trustDeed
        
    ];

    //--- conditions  ---//
    if (!['BusinessLoans', 'BusinessOverdraft'].includes(applicationType)) {
        docs.push(constants.documentTypes.taxInvoice);
        if (deposit) {
            docs.push(constants.documentTypes.depositReceipt);
        }
    }
    if(!['Consumer'].includes(applicationType)){
        docs.push(constants.unclassifiedDocuments);
    }
    if(applicationType == 'Consumer'){
        docs.push(constants.documentTypes.PaySlips);
    }

    if (['BusinessLoans', 'BusinessOverdraft'].includes(applicationType)) {
        docs.push(constants.documentTypes.payoutLetter);
    }

    if (isPrivateSale) {
        if (loanAmount && loanAmount > 150000) {
            docs.push({
                ...constants.documentTypes.externalDesktopValuation,
                required: true
            });
        } else {
            docs.push(constants.documentTypes.assetInspection);
        }

        docs.push({
            ...constants.documentTypes.proofOfSupplierBankDetails,
            required: true
        });
        docs.push({
            ...constants.documentTypes.proofOfOwnership,
            required: true
        })
    } else {
        docs.push(constants.documentTypes.proofOfSupplierBankDetails);
        docs.push(constants.documentTypes.assetInspection);
    }

    if (isPartnership) {
        docs.push(constants.documentTypes.certifiedPartnershipAgreement);
    }

    if (includeOptionalTypes) {
        docs.push(constants.documentTypes.guarantorStatutoryDeclaration);
        docs.push(constants.documentTypes.deedOfRelease);
        if (!docs.find(doc => doc.value === constants.documentTypes.proofOfOwnership.value)) {
            docs.push(constants.documentTypes.proofOfOwnership);
        }
        docs.push(constants.documentTypes.directionToPay);
        docs.push(constants.documentTypes.certifiedIds);
        docs.push(constants.documentTypes.ppsrDischargeCertificate);
        docs.push(constants.documentTypes.externalDesktopValuation);
        docs.push(constants.documentTypes.certificateOfCurrency);
        docs.push(constants.documentTypes.applicationForm);
        docs.push(constants.documentTypes.contract);
        docs.push(constants.documentTypes.specificCharges);
    }

    return docs;
}

export const getAllDefaultTags = (): DocumentTag[] => {
    return [
        constants.documentTypes.driversLicence,
        constants.documentTypes.privacyConsent,
        constants.documentTypes.bankStatements,
        constants.documentTypes.financials,
        constants.documentTypes.atoActivityStatements,
        constants.documentTypes.assetLiabilitiesStatement,
        constants.documentTypes.taxInvoice,
        constants.documentTypes.ppsr,
        constants.documentTypes.brokerInvoices,
        constants.documentTypes.payoutLetter,
        constants.documentTypes.trustDeed,
        constants.documentTypes.certificateOfCurrency,
        constants.documentTypes.proofOfSupplierBankDetails,
        constants.documentTypes.certifiedPartnershipAgreement,
        constants.documentTypes.depositReceipt,
        constants.documentTypes.guarantorStatutoryDeclaration,
        constants.documentTypes.deedOfRelease,
        constants.documentTypes.proofOfOwnership,
        constants.documentTypes.directionToPay,
        constants.documentTypes.certifiedIds,
        constants.documentTypes.ppsrDischargeCertificate,
        constants.documentTypes.specificCharges,
        constants.documentTypes.externalDesktopValuation,
        constants.documentTypes.internalDocuments,
        constants.documentTypes.creditorWatch,
        constants.documentTypes.applicationForm,
        constants.documentTypes.contract,
        constants.documentTypes.specificCharges,
        constants.documentTypes.approvalNotice,
        constants.documentTypes.assetInspection,
        constants.documentTypes.disburseFundsToIncumbentFinancier,
        constants.documentTypes.loanStatementFromIncumbentFinancier,
        constants.documentTypes.confirmationOfAssetAcquisition,
        constants.documentTypes.assetInspectionOnSite,
        constants.documentTypes.notExceedForcedLiquidation,
        constants.documentTypes.facialRecognition,
    ];
}

export const accreditationDocumentTags = (): DocumentTag[] => {
    return [
        constants.unclassifiedDocuments,
        constants.documentTypes.driversLicence,
        constants.documentTypes.professionalIndemnityInsurance,
        constants.documentTypes.industryMemberships,
        constants.documentTypes.executedBrokerAgreement,
        constants.documentTypes.bankDepositSlip,
        constants.documentTypes.introducerReferralAgreement,
    ]
}

export const getNotificationDocuments = (documents: GroupedDocument[]): {
    pendingDocs: NotificationGroupedDocuments[],
    inProgressDocs: NotificationGroupedDocuments[],
    acceptedDocs: NotificationGroupedDocuments[],
    rejectedDocs: NotificationGroupedDocuments[],
} => {
    const pendingDocs: NotificationGroupedDocuments[] = [];
    const inProgressDocs: NotificationGroupedDocuments[] = [];
    const rejectedDocs: NotificationGroupedDocuments[] = [];
    const acceptedDocs: NotificationGroupedDocuments[] = [];

    for (const g of documents) {
        if (!g.docs?.length) {
            if (g.status?.toLowerCase() === 'pending')  {
                pendingDocs.push({
                    ...g,
                    wholeGroup: true
                });
            } else if (g.status?.toLowerCase() === 'rejected') {
                rejectedDocs.push({
                    ...g,
                    wholeGroup: true
                });
            } else if (g.status?.toLowerCase() === 'accepted') {
                acceptedDocs.push({
                    ...g,
                    wholeGroup: true
                });
            }
        } else {
            const rejectedDoc = g.docs.filter(d => (d.metadata as Metadata)[g.value].toLowerCase() === 'rejected');
            const acceptedDoc = g.docs.filter(d => (d.metadata as Metadata)[g.value].toLowerCase() === 'accepted');
            const inProgressDoc = g.docs.filter(d => (d.metadata as Metadata)[g.value].toLowerCase() === 'in progress');

            if (rejectedDoc && rejectedDoc.length) {
                rejectedDocs.push({
                    ...g,
                    docs: rejectedDoc,
                    wholeGroup: false
                });
            }

            if (acceptedDoc && acceptedDoc.length) {
                acceptedDocs.push({
                    ...g,
                    docs: acceptedDoc,
                    wholeGroup: false
                })
            }

            if (inProgressDoc && inProgressDoc.length) {
                inProgressDocs.push({
                    ...g,
                    docs: inProgressDoc,
                    wholeGroup: false
                })
            }
        }
    }

    acceptedDocs.sort((a, b) => {
        if (a.wholeGroup !== b.wholeGroup) {
            return a.wholeGroup ? -1 : 1;
        }
        return 0;
    })

    return {
        pendingDocs,
        inProgressDocs,
        acceptedDocs,
        rejectedDocs
    }
}

export const getDocumentNotificationStrings = (group: GroupedDocument, maxLength: number = 48): DocumentNotificationData[] => {
    const results: DocumentNotificationData[] = [];
    if (maxLength < 19) { // too short
        return results;
    }
    if (group && group?.docs) {
        let groupName = group?.groupName ?? '';
        groupName = groupName;

        if (group?.docs && group?.docs?.length) {
            for (const doc of group?.docs) {
                let name = '';
                if (doc && (doc?.metadata as any)?.name) {
                    name = (doc?.metadata as any).name ?? '';
                    name = name;   
                    groupName = groupName;
                }
                results.push({
                    groupName: groupName,
                    space: maxLength - groupName.length - name.length,
                    docName: name
                });
            }
        } 
        else {
            results.push({
                groupName: groupName,
                space: 0,
                docName: ''
            });
        }
    }

    return results;
}

export const getUniqueDocumentTags = (data: AzureStorageDocument[]): DocumentTag[] => {
    let allTagValues: string[] = [];
    const allDefaultTags: DocumentTag[] = getAllDefaultTags();
    for (const doc of data) {
        allTagValues = [...allTagValues, ...Object.values(doc?.tags ?? {})];
    }
    allTagValues = [...new Set(allTagValues)];
    return allTagValues.map(value => {
        const existingTagObj = allDefaultTags.find(tag => tag.value === value);
        return {
            type: existingTagObj ? existingTagObj.type : value,
            value: value
        }
    }) as DocumentTag[];
}

export const unionExistingTagsWithDefaultTags = (data: AzureStorageDocument[]): DocumentTag[] => {
    let allTagValues: string[] = [];
    const allDefaultTags: DocumentTag[] = getAllDefaultTags();
    for (const doc of data) {
        allTagValues = [...allTagValues, ...Object.values(doc?.tags ?? {})];
    }
    allTagValues = [...new Set(allTagValues), ...allDefaultTags.map(t => t.value)];
    return allTagValues.map(value => {
        const existingTagObj = allDefaultTags.find(tag => tag.value === value);
        return {
            type: existingTagObj ? existingTagObj.type : value,
            value: value
        }
    }) as DocumentTag[];
}


export const formGroupedDocumentData = (rawData: AzureStorageDocument[], documentTypes: DocumentTag[] = [], customerName: string = ""): GroupedDocument[] => {
    let docs: GroupedDocument[] = [];
    let docsToMap: any[] = [];
    if (documentTypes && documentTypes.length) {
        docsToMap = documentTypes
    } else {
        // get all unique tags
        docsToMap = getUniqueDocumentTags(rawData)
    }
    docs = docsToMap.map((docType: DocumentTag) => {
        let group: GroupedDocument = {
            groupName: docType.type,
            description: docType?.description ?? '',
            value: docType.value,
            required: docType?.required ?? false,
            disableDocumentUpload: docType?.disableDocumentUpload ?? false,
            status: docType?.status ?? 'Pending',
            docs: []
        };
        if (docType?.nonStandardCondition) {
            group = {
                ...group,
                nonStandardCondition: !!docType?.nonStandardCondition,
            }
        }
        if (docType?.standardCondition) {
            group = {
                ...group,
                standardCondition: !!docType?.standardCondition,
            }
        }
        if (docType?.otherCondition) {
            group = {
                ...group,
                otherCondition: !!docType?.otherCondition,
            }
        }
        return group;
    });

    const foundContractObj = rawData.find(data => Object.values(data?.tags ?? {}).includes(constants.documentTypes.contract.value));
    if (foundContractObj && !docs.find(g => g.value == constants.documentTypes.contract.value)) {
        docs.push({
            groupName: constants.documentTypes.contract.type,
            description: (constants.documentTypes.contract.description + customerName),
            value: constants.documentTypes.contract.value,
            required: false,
            status: 'Pending',
            docs: []
        })
    }

    const foundApprovalNoticeObj = rawData.find(data => Object.values(data?.tags ?? {}).includes(constants.documentTypes.approvalNotice.value));
    if (foundApprovalNoticeObj && !docs.find(g => g.value == constants.documentTypes.approvalNotice.value)) {
        docs.push({
            groupName: constants.documentTypes.approvalNotice.type,
            description: constants.documentTypes.approvalNotice.description,
            value: constants.documentTypes.approvalNotice.value,
            status: 'Pending',
            required: false,
            docs: []
        })
    }

    const foundTaxInvoiceObj = rawData.find(data => Object.values(data?.tags ?? {}).includes(constants.documentTypes.taxInvoice.value));
    if (foundTaxInvoiceObj && !docs.find(g => g.value == constants.documentTypes.taxInvoice.value)) {
        docs.push({
            groupName: constants.documentTypes.taxInvoice.type,
            description: constants.documentTypes.taxInvoice.description,
            value: constants.documentTypes.taxInvoice.value,
            status: 'Pending',
            required: false,
            docs: []
        })
    }

    const foundPrivacyConsentObj = rawData.find(data => Object.values(data?.tags ?? {}).includes(constants.documentTypes.privacyConsent.value));
    if (foundPrivacyConsentObj && !docs.find(g => g.value == constants.documentTypes.privacyConsent.value)) {
        docs.push({
            groupName: constants.documentTypes.privacyConsent.type,
            description: constants.documentTypes.privacyConsent.description,
            value: constants.documentTypes.privacyConsent.value,
            status: 'Pending',
            required: false,
            docs: []
        })
    }

    const foundFacialRecognitionObj = rawData.find(data => Object.values(data?.tags ?? {}).includes(constants.documentTypes.facialRecognition.value));
    if (foundFacialRecognitionObj && !docs.find(g => g.value == constants.documentTypes.facialRecognition.value)) {
        docs.push({
            groupName: constants.documentTypes.facialRecognition.type,
            description: constants.documentTypes.facialRecognition.description,
            value: constants.documentTypes.facialRecognition.value,
            status: 'Pending',
            required: false,
            docs: []
        })
    }

    for (const data of rawData) {
        if (data?.tags) {
            const tagValues = Object.values(data.tags);

            let pushed = false;
            for (let i = 0; i < docs.length; i++) {
                if (tagValues.includes(docs[i].value)) {
                    docs[i].docs.push(data);
                    pushed = true;
                }
            }
        }
    }

    // review status
    for (let i = 0; i < docs.length; i++) {
        docs[i].status = 
            ['Accepted', 'Rejected'].includes(docs[i].status ?? '') ? 
                docs[i].status : 
                docs[i].docs.length ? 'In Progress' : 'Pending';
    }

    return docs.sort((a, b) => {
        if (a.required !== b.required) {
            return a.required ? -1 : 1;
        } else {
            return a.groupName.localeCompare(b.groupName);
        }
    });
}
